import React from 'react';
import { useNls, type NlsContextValue } from '../../../nls/nlsProvider';
import type { OpsTemplateField } from '../../../types/ops';
import { fromLocalDateTimeInput } from '../../../utils/localDateTimeInput';
import { formatMoney } from '../../../utils/formatMoney';
import { CashCountFieldValue } from './CashCountFieldValue';

// eslint-disable-next-line react-refresh/only-export-components
export function formatTimestampDisplay(
  val: unknown,
  locale: string = 'en',
): string {
  if (val == null || val === '') return '—';
  let d: Date | null = null;

  if (val instanceof Date) {
    d = val;
  } else if (typeof val === 'number') {
    d = new Date(val);
    if (Number.isNaN(d.getTime())) return String(val);
  } else if (typeof val === 'string') {
    const ms = fromLocalDateTimeInput(val);
    if (ms !== null) {
      d = new Date(ms);
    } else {
      const parsed = new Date(val);
      if (!Number.isNaN(parsed.getTime())) {
        d = parsed;
      } else {
        return val;
      }
    }
  } else if (
    typeof val === 'object' &&
    'toDate' in (val as Record<string, unknown>)
  ) {
    const fn = (val as { toDate?: () => Date }).toDate;
    if (typeof fn === 'function') {
      const candidate = fn.call(val);
      if (candidate instanceof Date && !Number.isNaN(candidate.getTime())) {
        d = candidate;
      }
    }
  }

  if (!d || Number.isNaN(d.getTime())) return '—';

  return new Intl.DateTimeFormat(locale, {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(d);
}

// eslint-disable-next-line react-refresh/only-export-components
export function formatRecordValueText(
  field: OpsTemplateField | undefined,
  value: unknown,
  t: NlsContextValue['t'],
  locale: string = 'en',
): string {
  const emptyText = t('NLSOpsValueEmpty');
  if (value === undefined || value === null || value === '') {
    return emptyText;
  }

  if (!field) {
    if (typeof value === 'boolean') {
      return value ? t('NLSOpsYes') : t('NLSOpsNo');
    }
    return String(value);
  }

  switch (field.type) {
    case 'YES_NO':
    case 'ACKNOWLEDGE': {
      if (value === true || value === 'YES' || value === 'true') {
        return t('NLSOpsYes');
      }
      if (value === false || value === 'NO' || value === 'false') {
        return t('NLSOpsNo');
      }
      return emptyText;
    }

    case 'DATE_TIME':
      return formatTimestampDisplay(value, locale);

    case 'CHOICE':
      return String(value);

    case 'READING':
    case 'NUMBER': {
      const numStr = String(value);
      return field.unit ? `${numStr} ${field.unit}` : numStr;
    }

    case 'PHOTO': {
      const photos = Array.isArray(value) ? value : [value];
      return String(photos.length);
    }

    case 'CASH_COUNT': {
      if (value && typeof value === 'object') {
        const v = value as { countedMinor?: number };
        if (typeof v.countedMinor === 'number') {
          return formatMoney(v.countedMinor);
        }
      }
      return emptyText;
    }
  }
}

export interface RecordFieldValueProps {
  field?: OpsTemplateField;
  value: unknown;
  photoUrlsByPath: Record<string, string>;
  currency?: string | null;
}

export const RecordFieldValue: React.FC<RecordFieldValueProps> = ({
  field,
  value,
  photoUrlsByPath,
  currency,
}) => {
  const { t, locale } = useNls();

  if (field?.type === 'CASH_COUNT') {
    return (
      <CashCountFieldValue
        field={field}
        value={value}
        currency={currency}
      />
    );
  }

  if (field?.type === 'PHOTO') {
    const rawItems = Array.isArray(value)
      ? value
      : typeof value === 'string' && value
        ? [value]
        : [];
    const paths = rawItems.filter(
      (item): item is string => typeof item === 'string' && item.length > 0,
    );

    if (paths.length === 0) {
      return (
        <span className="opsRegistersKVVal opsRegistersFigures">
          {t('NLSOpsValueEmpty')}
        </span>
      );
    }

    return (
      <div className="opsRegistersPhotoGrid">
        {paths.map((p) => {
          const resolvedUrl = photoUrlsByPath[p];
          if (!resolvedUrl) {
            return (
              <span key={p} className="opsRegistersFieldHint">
                {t('NLSOpsPhotoLoadingPlaceholder')}
              </span>
            );
          }
          return (
            <img
              key={p}
              src={resolvedUrl}
              alt={field.label}
              className="opsRegistersPhotoThumb"
            />
          );
        })}
      </div>
    );
  }

  const formatted = formatRecordValueText(field, value, t, locale);

  return (
    <span className="opsRegistersKVVal opsRegistersFigures">{formatted}</span>
  );
};
